import React, { useEffect, useState } from 'react';
import {
  Banknote,
  CircleDollarSign,
  Download,
  FileText,
  GraduationCap,
  HardDrive,
  WalletCards,
} from 'lucide-react';
import teacherBillingService from '../../services/teacherBillingService';
import { TeacherBillingSummary, TeacherInvoice } from '../../types/billing';
import Button from '../ui/Button';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import Input from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import MetricCard from '../ui/MetricCard';
import StatusBadge from '../ui/StatusBadge';
import Surface from '../ui/Surface';
import toast from 'react-hot-toast';

const money = new Intl.NumberFormat('ar-EG', {
  style: 'currency',
  currency: 'EGP',
  maximumFractionDigits: 2,
});

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 GB';
  const gb = bytes / 1024 ** 3;
  if (gb < 1) return `${(bytes / 1024 ** 2).toLocaleString('ar-EG', {
    maximumFractionDigits: 1,
  })} MB`;
  return `${gb.toLocaleString('ar-EG', { maximumFractionDigits: 1 })} GB`;
};

const formatDate = (value: any): string => {
  const date = value?.toDate ? value.toDate() : value instanceof Date ? value : null;
  return date
    ? new Intl.DateTimeFormat('ar-EG', { dateStyle: 'medium' }).format(date)
    : 'غير محدد';
};

const invoiceStatusLabel: Record<TeacherInvoice['status'], string> = {
  draft: 'مسودة',
  issued: 'صادرة',
  paid: 'مدفوعة',
  overdue: 'متأخرة',
  cancelled: 'ملغاة',
  adjusted: 'معدلة',
};

const invoiceStatusTone: Record<
  TeacherInvoice['status'],
  'neutral' | 'info' | 'success' | 'warning' | 'danger'
> = {
  draft: 'neutral',
  issued: 'info',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'neutral',
  adjusted: 'warning',
};

const TeacherBillingOverview: React.FC = () => {
  const [summary, setSummary] = useState<TeacherBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [limit, setLimit] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const value = await teacherBillingService.getSummary();
      setSummary(value);
      setLimit(value.account.spendingLimitEgp?.toString() || '');
    } catch (error) {
      console.error('Failed to load teacher billing summary', error);
      toast.error('تعذر تحميل بيانات محاسبة المدرس');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const saveBudget = async () => {
    const parsedLimit = limit.trim() ? Number(limit) : null;
    if (parsedLimit !== null && (!Number.isFinite(parsedLimit) || parsedLimit < 0)) {
      toast.error('أدخل حد إنفاق صحيحًا أو اترك الحقل فارغًا');
      return;
    }

    setSaving(true);
    try {
      await teacherBillingService.updateBudget({
        spendingLimitEgp: parsedLimit,
        alertThresholds: [70, 90, 100],
      });
      toast.success('تم تحديث حد الإنفاق والتنبيهات');
      await load();
    } catch (error) {
      console.error('Failed to update teacher billing budget', error);
      toast.error('تعذر تحديث حد الإنفاق');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Surface className='flex min-h-[16rem] items-center justify-center'>
        <LoadingSpinner size='large' />
      </Surface>
    );
  }

  if (!summary) {
    return (
      <Surface>
        <EmptyState
          icon={FileText}
          title='لا توجد بيانات محاسبة متاحة'
          description='تعذر تحميل ملخص الاشتراك والاستخدام لهذا المدرس.'
          action={
            <Button variant='secondary' onClick={load}>
              إعادة المحاولة
            </Button>
          }
        />
      </Surface>
    );
  }

  const students = summary.students ?? {
    usage: summary.activeStudentCount,
    included: summary.currentPlan.includedActiveStudents,
    extra: summary.additionalActiveStudents,
    blockSize: summary.currentPlan.students?.blockSize ?? 1,
    blocks: summary.additionalActiveStudents,
    blockPriceEgp: summary.currentPlan.activeStudentOverageEgp ?? 0,
    amountEgp: summary.estimatedOverageEgp,
    unit: 'student' as const,
  };
  const storage = summary.storage;
  const bandwidth = summary.bandwidth;
  const billingCycle = summary.billingCycle || 'monthly';
  const isAnnual = billingCycle === 'yearly';
  const estimatedInvoiceEgp =
    summary.estimatedInvoiceEgp ??
    summary.account.baseMonthlyFeeEgp + summary.estimatedOverageEgp;

  return (
    <section className='space-y-6'>
      <Surface padding='lg'>
        <div className='flex flex-col justify-between gap-4 lg:flex-row lg:items-start'>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-primary-600 dark:text-primary-300'>
              محاسبة المدرس
            </p>
            <h2 className='mt-1 text-2xl font-bold text-secondary-950 dark:text-white'>
              الاشتراك واستخدام الموارد
            </h2>
            <p className='mt-2 max-w-3xl text-sm leading-6 text-secondary-500 dark:text-secondary-400'>
              الفترة الحالية {summary.currentPeriod}. يحسب كل طالب نشط مرة واحدة
              مهما تكرر نشاطه، والتخزين يحسب حسب الحجم الحالي فوق حد الباقة.
            </p>
          </div>
          <div className='flex flex-wrap gap-2'>
            <StatusBadge tone='info'>
              دورة الدفع: {isAnnual ? 'سنوي' : 'شهري'}
            </StatusBadge>
            <StatusBadge tone='success'>
              تقدير فاتورة الاستخدام: {money.format(estimatedInvoiceEgp)}
            </StatusBadge>
          </div>
        </div>
        {summary.upgradeRecommendation && (
          <div className='mt-5 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-950/30 dark:text-primary-100'>
            {summary.upgradeRecommendation.reasonAr}{' '}
            التقدير على Pro: {money.format(summary.upgradeRecommendation.estimatedAmountEgp)}
            {summary.upgradeRecommendation.savingsEgp > 0
              ? `، وفر تقريبي ${money.format(summary.upgradeRecommendation.savingsEgp)}.`
              : '.'}
          </div>
        )}
      </Surface>

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5'>
        <MetricCard
          icon={Banknote}
          label={isAnnual ? 'الاشتراك السنوي' : 'الاشتراك الشهري'}
          value={money.format(summary.subscriptionBaseAmountEgp ?? summary.account.baseMonthlyFeeEgp)}
          note={
            isAnnual
              ? `التجديد التالي ${formatDate(summary.nextBaseBillingAt)}`
              : 'يدخل ضمن فاتورة الاشتراك الشهرية'
          }
          tone='green'
        />
        <MetricCard
          icon={CircleDollarSign}
          label='الزيادة المتوقعة'
          value={money.format(summary.estimatedOverageEgp)}
          note={`طلاب ${money.format(students.amountEgp)} + تخزين ${money.format(storage?.amountEgp ?? 0)}`}
          tone='blue'
        />
        <MetricCard
          icon={GraduationCap}
          label='الطلاب النشطون'
          value={summary.activeStudentCount.toLocaleString('ar-EG')}
          note={`${students.included} مشمول، ${students.blocks} شريحة إضافية`}
          tone='amber'
        />
        <MetricCard
          icon={HardDrive}
          label='التخزين الحالي'
          value={formatBytes(storage?.usage ?? summary.infrastructureUsage.storageBytes)}
          note={
            storage
              ? `${formatBytes(storage.included)} مشمول، زيادة ${money.format(storage.amountEgp)}`
              : 'بانتظار snapshot التخزين'
          }
          tone='green'
        />
        <MetricCard
          icon={Download}
          label='التحميل الشهري'
          value={formatBytes(bandwidth?.usage ?? summary.infrastructureUsage.egressBytes)}
          note={
            bandwidth?.billingEnabled
              ? `زيادة ${money.format(bandwidth.amountEgp)}`
              : 'قياس التحميل غير مفعل للفوترة بعد'
          }
          tone='blue'
        />
      </div>

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <MetricCard
          icon={WalletCards}
          label='إيرادات الطلاب'
          value={money.format(summary.studentRevenueEgp)}
          note='دفتر مالي للمدرس، وليس تحصيلًا عبر المنصة'
          tone='green'
        />
        <MetricCard
          icon={Banknote}
          label='مستحقات الطلاب'
          value={money.format(summary.outstandingStudentPaymentsEgp)}
          note='مبالغ غير مسددة في دفتر المدرس'
          tone='red'
        />
      </div>

      <div className='grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]'>
        <Surface padding='lg'>
          <h3 className='font-bold text-secondary-950 dark:text-white'>
            طريقة احتساب الاستخدام
          </h3>
          <div className='mt-4 grid gap-4 sm:grid-cols-3'>
            <div className='rounded-xl bg-secondary-50 p-4 dark:bg-secondary-900'>
              <p className='text-xs text-secondary-500'>المشمول</p>
              <p className='mt-1 text-xl font-bold'>{students.included}</p>
            </div>
            <div className='rounded-xl bg-secondary-50 p-4 dark:bg-secondary-900'>
              <p className='text-xs text-secondary-500'>الإضافي</p>
              <p className='mt-1 text-xl font-bold'>{students.extra}</p>
            </div>
            <div className='rounded-xl bg-secondary-50 p-4 dark:bg-secondary-900'>
              <p className='text-xs text-secondary-500'>سعر شريحة الطلاب</p>
              <p className='mt-1 text-xl font-bold'>
                {money.format(students.blockPriceEgp)}
              </p>
              <p className='mt-1 text-xs text-secondary-500'>
                كل {students.blockSize} طلاب فوق الحد
              </p>
            </div>
          </div>
          <p className='mt-4 text-sm leading-6 text-secondary-500'>
            النشاط المؤهل هو الحضور أو تسليم واجب أو دفعة أو مشاركة فعلية في جلسة.
            الحسابات التجريبية والمحذوفة لا تدخل في العدد.
          </p>
        </Surface>

        <Surface padding='lg' className='space-y-4'>
          <div>
            <h3 className='font-bold text-secondary-950 dark:text-white'>
              حد الإنفاق والتنبيهات
            </h3>
            <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
              التنبيهات القياسية عند 70% و90% و100% من الحد الشهري.
            </p>
          </div>
          <Input
            label='حد الإنفاق الشهري الاختياري'
            type='number'
            min='0'
            inputMode='decimal'
            value={limit}
            onChange={event => setLimit(event.target.value)}
            placeholder='مثال: 1500'
            hint='اتركه فارغًا لإلغاء الحد.'
          />
          <Button
            className='w-full'
            onClick={saveBudget}
            loading={saving}
            variant='primary'
          >
            حفظ الحد
          </Button>
        </Surface>
      </div>

      <Surface padding='lg'>
        <div className='mb-4'>
          <h3 className='font-bold text-secondary-950 dark:text-white'>
            آخر فواتير المنصة
          </h3>
          <p className='mt-1 text-sm text-secondary-500 dark:text-secondary-400'>
            الفواتير الصادرة أو المعدلة لا تتغير تلقائيًا بعد الإقفال.
          </p>
        </div>
        <DataTable
          caption='آخر فواتير اشتراك المنصة'
          columns={[
            {
              key: 'period',
              header: 'الفترة',
              cell: invoice => invoice.period,
            },
            {
              key: 'status',
              header: 'الحالة',
              cell: invoice => (
                <StatusBadge tone={invoiceStatusTone[invoice.status]}>
                  {invoiceStatusLabel[invoice.status]}
                </StatusBadge>
              ),
            },
            {
              key: 'total',
              header: 'الإجمالي',
              className: 'text-left tabular-nums',
              cell: invoice => money.format(invoice.totalEgp),
            },
          ]}
          rows={summary.recentInvoices}
          rowKey={invoice => invoice.id}
          emptyState={
            <EmptyState
              icon={FileText}
              title='لا توجد فواتير بعد'
              description='ستظهر هنا الفواتير الشهرية بعد إصدار أول دورة فوترة.'
              compact
            />
          }
        />
      </Surface>
    </section>
  );
};

export default TeacherBillingOverview;
